import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Input, Table, Space, Select, Typography, Menu, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { Row, Col, Button, Modal, Dropdown } from "react-bootstrap";
import {
  manualOptions,
  backendOptions,
  filterOptions,
} from "../../entities/Constants/DashboardConstants";
import {
  deleteLeads,
  fetchLeads,
  filterLeads,
  setToastForAddLead,
  updateLeadStatus,
  updatePagination,
  updateSortOrder,
} from "../../features/Lead/leadsSlice";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { formatDate } from "../../utils/Utils";
import { resetToastConfig } from "../../features/Lead/leadsSlice";
import ToastNotification from "../../components/Common/ToastNotification";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";

const { Option } = Select;
const { Text } = Typography;

const LeadPage = () => {
  const { trans } = useCustomTranslation();
  useFetchCoachProfile();

  const { profileData } = useSelector((state) => state.profile);
  const { toastConfig } = useSelector((state) => state.leads);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const coachId = profileData?.coachId;

  const { leads, loading, pagination, sortOrder, totalCount, error } =
    useSelector((state) => state.leads);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [deleteType, setDeleteType] = useState("");
  const [recordToDelete, setRecordToDelete] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [statusUpdates, setStatusUpdates] = useState({});

  // Fetch leads on component mount and when state changes
  useEffect(() => {
    if (coachId) {
      dispatch(
        fetchLeads({
          coachId: coachId,
          pageNumber: pagination.current,
          pageSize: pagination.pageSize,
          sortOrder,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, sortOrder, coachId]);

  useEffect(() => {
    if (toastConfig && toastConfig.type === "success") {
      const timer = setTimeout(() => {
        dispatch(resetToastConfig());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toastConfig, dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (coachId) {
        dispatch(
          filterLeads({
            coachId,
            name: searchQuery,
            status: statusFilter,
            sortOrder,
          })
        );
      }
    }, 300);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchQuery,
    statusFilter,
    pagination.current,
    pagination.pageSize,
    sortOrder,
    dispatch,
  ]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSortChange = ({ key }) => {
    if (coachId) {
      dispatch(updateSortOrder(key));
      dispatch(
        filterLeads({
          coachId,
          name: debouncedSearch,
          status: statusFilter,
          sortOrder: key,
        })
      );
    }
  };

  const handleRowClick = (record, e) => {
    if (
      e.target.closest(".action-buttons") ||
      e.target.closest(".ant-select")
    ) {
      return;
    }
    navigate(`/leads/editLead/${record.key}`, {
      state: { ...record, isReadOnly: record.status === "Won" },
    });
  };

  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;

    dispatch(
      updatePagination({
        current,
        pageSize,
      })
    );
    dispatch(
      filterLeads({
        coachId: coachId,
        name: debouncedSearch,
        status: statusFilter,
      })
    );
  };

  const handleStatusFilterChange = (e) => {
    const filterKey = e.key === "All" ? "" : e.key;
    setStatusFilter(filterKey);

    dispatch(
      updatePagination({
        current: 1,
      })
    );

    dispatch(
      filterLeads({
        coachId,
        name: debouncedSearch,
        status: filterKey,
        pageNumber: 1,
        pageSize: pagination.pageSize,
        sortOrder,
      })
    );
  };

  const handleStatusChange = async (value, recordKey) => {
    try {
      await dispatch(
        updateLeadStatus({ coachId, leadId: recordKey, status: value })
      ).unwrap();

      setStatusFilter("");

      dispatch(updatePagination({ current: 1 }));

      dispatch(
        fetchLeads({
          coachId: coachId,
          pageNumber: pagination.current,
          pageSize: pagination.pageSize,
          sortOrder,
        })
      );
    } catch (error) {
      dispatch(
        setToastForAddLead({
          type: "error",
          message: trans("LeadPage.statusUpdateFailed"),
          heading: trans("Common.failed"),
        })
      );
    }
  };

  const handleDeleteClick = (record, e) => {
    e.stopPropagation();
    setRecordToDelete([record.key]);
    setDeleteType("single");
    setShowModal(true);
  };

  const handleMultipleDeleteClick = () => {
    setRecordToDelete(selectedRowKeys);
    setDeleteType("multiple");
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setRecordToDelete(null);
    setDeleteType("");
  };
  const handleConfirmDelete = async () => {
    if (!recordToDelete || recordToDelete.length === 0) return;

    try {
      await dispatch(deleteLeads(recordToDelete)).unwrap();

      dispatch(
        setToastForAddLead({
          type: "success",
          message: trans(
            recordToDelete.length === 1
              ? "LeadPage.singleLeadDeleted"
              : "LeadPage.multipleLeadsDeleted"
          ),
          heading: trans("Common.success"),
        })
      );
      setStatusFilter("");
      dispatch(updatePagination({ current: 1 }));

      dispatch(
        fetchLeads({
          coachId: coachId,
          pageNumber: pagination.current,
          pageSize: pagination.pageSize,
          sortOrder,
        })
      );
    } catch (error) {
      dispatch(
        setToastForAddLead({
          type: "error",
          message: trans("LeadPage.deleteFailed"),
          heading: trans("Common.failed"),
        })
      );
    }

    if (deleteType === "multiple") {
      setSelectedRowKeys([]);
    }
    setShowModal(false);
    setRecordToDelete(null);
    setDeleteType("");
  };

  const onSelectChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  const menuItems = filterOptions.map((option) => ({
    key: option.value,
    label: option.label,
  }));

  const columns = [
    {
      title: trans("Common.name"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <span>
          {record.name?.charAt(0)?.toUpperCase() +
            record.name?.slice(1)?.toLowerCase()}
        </span>
      ),
    },
    { title: trans("Common.phoneNumber"), dataIndex: "phone", key: "phone" },
    {
      title: trans("Common.status"),
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const currentStatus = statusUpdates[record.key] || status;

        const isBackendControlled = backendOptions.some(
          (option) => option.value === currentStatus
        );
        let statusColor = "#D3F8E2";

        if (isBackendControlled) {
          const selectedBackendOption = backendOptions.find(
            (option) => option.value === currentStatus
          );
          if (selectedBackendOption) {
            statusColor = selectedBackendOption.color;
          }
        } else {
          const selectedOption = manualOptions.find(
            (option) => option.value === currentStatus
          );
          statusColor = selectedOption ? selectedOption.color : statusColor;
        }

        return (
          <div
            className={`custom-select-wrapper ${
              isBackendControlled ? "backend-controlled" : ""
            }`}
          >
            <Select
              value={currentStatus}
              disabled={isBackendControlled}
              onChange={(value) => {
                handleStatusChange(value, record.key);
              }}
              className="custom-select dropdown-style"
              popupClassName="custom-select-dropdown"
              style={{
                backgroundColor: `${statusColor}`,
                color: isBackendControlled ? "#888" : "black",
                cursor: isBackendControlled ? "not-allowed" : "pointer",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {isBackendControlled ? (
                <Option
                  key={currentStatus}
                  value={currentStatus}
                  className="backend-controlled"
                >
                  {currentStatus}
                </Option>
              ) : (
                manualOptions
                  .filter(
                    (option) =>
                      currentStatus !== "Offer sent" ||
                      option.value !== "New Lead"
                  )
                  .map((option) => (
                    <Option
                      key={option.value}
                      value={option.value}
                      className={`option-style custom-select-option ${
                        option.value === currentStatus ? "selected" : ""
                      }`}
                      style={{
                        backgroundColor: option.color,
                      }}
                    >
                      {option.label}
                    </Option>
                  ))
              )}
            </Select>
          </div>
        );
      },
    },
    {
      title: trans("Common.arrived"),
      dataIndex: "createdOn",
      key: "arrived",
      render: (createdOn) => {
        return formatDate(createdOn);
      },
    },
    {
      title: trans("Common.actions"),
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          {record.status !== "Won" && (
            <>
              <Tooltip title={trans("Common.edit")}>
                <Button
                  type="button"
                  className="action-buttons"
                  onClick={() =>
                    navigate(`/leads/editLead/${record.key}`, { state: record })
                  }
                >
                  <i className="bi bi-pencil"></i>
                </Button>
              </Tooltip>
              <Tooltip title={trans("Common.delete")}>
                <Button
                  type="button"
                  className="action-buttons"
                  onClick={(e) => handleDeleteClick(record, e)}
                >
                  <i className="bi bi-trash"></i>
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];
  const getShowingText = () => {
    const { current, pageSize } = pagination;
    const start = (current - 1) * pageSize + 1;
    const end = Math.min(current * pageSize, totalCount);
    return totalCount
      ? `${trans("Common.showing")} ${start} ${trans(
          "Common.to"
        )} ${end} ${trans("Common.of")} ${totalCount} ${trans(
          "Common.results"
        )}`
      : trans("Common.noResults");
  };

  const sortMenuItems = [
    { key: "desc", label: trans("Common.newest") },
    { key: "asc", label: trans("Common.oldest") },
  ];

  return (
    <div className="lead-page">
      {/* Header Section */}

      {/* Filter Section */}
      <div className="filter-section">
        <div className="filter-container">
          <div className="filter-header">
            <i className="bi bi-filter-left"></i>
            <span className="filter-text">Filter:</span>
          </div>
          <Menu
            mode="horizontal"
            selectedKeys={[statusFilter || "All"]}
            onClick={handleStatusFilterChange}
            className="filter-menu custom-menu"
            items={menuItems}
          />

          <div className="button-inside-menu">
            <Space>
              <Button
                className="common-theme-button-empty"
                onClick={() => navigate("addNewOffer")}
              >
                <i className="bi bi-send icon-blue-colour"></i>{" "}
                <span className="icon-blue-colour">
                  {trans("LeadPage.sendOffer")}
                </span>
              </Button>
              <Button
                className="common-theme-button"
                onClick={() => navigate("addNewLead")}
              >
                <i className="bi bi-plus"></i> {trans("LeadPage.createLead")}
              </Button>
            </Space>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="table-container">
        <Row className="pb-3 d-flex">
          <Col xs={4}>
            <Input
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={trans("Common.searchByName")}
              allowClear
              className="rounded-0 search-box common-theme-colour "
            />
          </Col>

          <Col
            xs="auto"
            className="ms-auto d-flex justify-content-end filter-lead"
          >
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                className="filter-dropdown-button common-theme-button"
              >
                <i className="bi bi-filter-left"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {sortMenuItems.map((item) => (
                  <Dropdown.Item
                    key={item.key}
                    onClick={() => handleSortChange({ key: item.key })}
                    className={sortOrder === item.key ? "bg-light" : ""}
                  >
                    {item.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
        <Row className=" align-items-center justify-content-between">
          <Col xs="auto">
            {selectedRowKeys.length > 0 && (
              <Button
                className="deleteButton"
                onClick={handleMultipleDeleteClick}
              >
                <i className="bi bi-trash"></i> {trans("Common.delete")}
              </Button>
            )}
          </Col>
        </Row>
        <Table
          className="custom-table"
          columns={columns}
          dataSource={leads}
          loading={loading}
          locale={{
            emptyText: error
              ? trans("LeadPage.fetchError")
              : trans("LeadPage.noResults"),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            position: ["bottomRight"],
            showTotal: () => (
              <div className="showing-text">
                <Text>{getShowingText()}</Text>
              </div>
            ),
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: (e) => {
              handleRowClick(record, e);
            },
          })}
          rowClassName="clickable-row"
          rowSelection={{
            selectedRowKeys,
            onChange: onSelectChange,
            getCheckboxProps: (record) => ({
              disabled: record.status === "Won",
            }),
          }}
          bordered={false}
          components={{
            table: (props) => (
              <table
                {...props}
                style={{
                  ...props.style,
                  borderCollapse: "separate",
                  borderSpacing: "0 10px",
                }}
              />
            ),
            header: {
              cell: (props) => {
                return (
                  <th
                    {...props}
                    style={{
                      ...props.style,
                      background: "transparent",
                      borderColor: "#FCFBFFCC",
                      borderStyle: "solid",
                      fontFamily: "Proxima-Nova-Regular",
                      borderWidth: "8px 0px",
                      padding: "12px 16px",
                      // Border radius for header cells
                      borderRadius:
                        props.children === columns[0].title
                          ? "12px 0 0 12px"
                          : props.children === columns[columns.length - 1].title
                          ? "0 12px 12px 0"
                          : "0",
                    }}
                  />
                );
              },
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    fontFamily: "Proxima-Nova-Regular",
                    background: "transparent",
                  }}
                />
              ),
            },
            body: {
              cell: (props) => (
                <td
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    borderColor: "transparent",
                    fontFamily: "Proxima-Nova-Regular",
                    borderStyle: "solid",
                    borderWidth: "8px 0px",
                    padding: "12px 16px",
                    // Border radius for body cells
                    borderRadius:
                      props.index % columns.length === 0
                        ? "12px 0 0 12px"
                        : props.index % columns.length === columns.length - 1
                        ? "0 12px 12px 0"
                        : "0",
                  }}
                />
              ),
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    fontFamily: "Proxima-Nova-Regular",
                    margin: "10px 0",
                  }}
                />
              ),
            },
          }}
        />
      </div>

      {/* Modal for Deletion */}
      <Modal
        show={showModal}
        onHide={handleModalClose}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {trans("Common.confirmation")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{trans("Common.areYouSureDeleteLead")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleModalClose}
            className="back_button w-25 p-1"
          >
            {trans("Common.cancel")}
          </Button>
          <Button
            className="saveButton w-25 ml-2"
            onClick={handleConfirmDelete}
          >
            {trans("Common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
      {toastConfig && toastConfig.type === "success" && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default LeadPage;
